/*
  Central registry of live websocket telemetry subscriptions, used for debugging
  the "too many subscriptions" problem.

  - every TimeseriesSubsciption is registered when it subscribes and removed when it unsubscribes
  - each change is logged with the entity label, and a summary table is printed after a burst of changes
  - window.agSubscriptions() prints the currently active subscriptions
  - window.agDebugSubscriptions(true|false) toggles logging at runtime (persisted in localStorage)

  To silence the logging permanently, set debug.enabled to false in websocket.config.ts.
*/
import { Subject } from 'rxjs'
import { WEBSOCKET_CONFIG } from '../websocket.config'

const SUBSCRIPTION_DEBUG = WEBSOCKET_CONFIG.debug

const STORAGE_KEY = 'ag_debug_ws'

export interface ISubscriptionInfo {
  label: string
  entityId: string
  entityType: string
  scope: string
  keys: string[]
  source: string
  since: number
  retries?: number
  // number of callers sharing this server subscription
  owners?: number
}

function isEnabled () {
  try {
    let value = localStorage.getItem(STORAGE_KEY)
    if (value == '1') return true
    if (value == '0') return false
  } catch (err) {}
  return SUBSCRIPTION_DEBUG.enabled
}

function formatAge (since: number) {
  let seconds = Math.round((Date.now() - since) / 1000)
  if (seconds < 60) return seconds + 's'
  let minutes = Math.floor(seconds / 60)
  if (minutes < 60) return minutes + 'm ' + (seconds % 60) + 's'
  return Math.floor(minutes / 60) + 'h ' + (minutes % 60) + 'm'
}

function formatKeys (keys: string[]) {
  if (keys.length <= 4) return keys.join(',')
  return keys.slice(0, 3).join(',') + ',… (' + keys.length + ')'
}

class SubscriptionRegistry {
  private active = new Map<object, ISubscriptionInfo>()
  // emits whenever a subscription is added or removed
  changed = new Subject<void>()
  private labels: {[entityId: string]: string} = {}
  private summaryTimer: number = null

  // entity labels, used when a subscription is created without a label (e.g. plots)
  setEntityLabel (entityId: string, label: string) {
    this.labels[entityId] = label
  }
  labelFor (entityId: string): string | undefined {
    return this.labels[entityId]
  }

  // silent: the subscription is only re-subscribing with new settings (e.g. changed time window)
  // socketCommands: number of commands currently registered on the websocket, to compare with
  register (owner: object, info: ISubscriptionInfo, silent=false, socketCommands?: number) {
    this.active.set(owner, info)
    if (!silent) this.log('+ SUB  ', '#2e7d32', info, socketCommands)
    this.changed.next()
  }

  unregister (owner: object, silent=false, socketCommands?: number) {
    let info = this.active.get(owner)
    if (!info) return
    this.active.delete(owner)
    if (!silent) this.log('- UNSUB', '#c62828', info, socketCommands)
    this.changed.next()
  }

  list (): ISubscriptionInfo[] {
    return Array.from(this.active.values())
  }

  get (owner: object): ISubscriptionInfo | undefined {
    return this.active.get(owner)
  }

  count () {
    return this.active.size
  }

  // logs a single visibility/subscription related message using the same on/off switch
  debug (message: string, ...args: any[]) {
    if (!isEnabled()) return
    console.log('%c' + message, 'color: #6a1b9a', ...args)
  }

  private log (prefix: string, color: string, info: ISubscriptionInfo, socketCommands?: number) {
    if (!isEnabled()) return
    let count = this.active.size + ' active'
    if (socketCommands != undefined) count += ', ' + socketCommands + ' socket commands'
    console.log(
      '%c' + prefix + ' %c' + info.label + '%c (' + info.entityType + ') ' + info.scope + ' [' + formatKeys(info.keys) + '] ' + info.source + ' · ' + count,
      'color: ' + color + '; font-weight: bold', 'font-weight: bold', 'color: inherit; font-weight: normal'
    )
    this.scheduleSummary()
  }

  private scheduleSummary () {
    if (this.summaryTimer) window.clearTimeout(this.summaryTimer)
    this.summaryTimer = window.setTimeout(() => {
      this.summaryTimer = null
      this.printSummary()
    }, SUBSCRIPTION_DEBUG.summaryDelay)
  }

  printSummary () {
    let rows = this.list().sort((a, b) => a.label.localeCompare(b.label)).map(info => ({
      label: info.label,
      type: info.entityType,
      scope: info.scope,
      keys: formatKeys(info.keys),
      source: info.source,
      age: formatAge(info.since),
      owners: info.owners || 0,
      retries: info.retries || 0,
      id: info.entityId
    }))
    console.groupCollapsed('%c' + rows.length + ' active websocket subscriptions', 'color: #1565c0; font-weight: bold')
    if (rows.length > 0) console.table(rows)
    console.groupEnd()
    if (rows.length > SUBSCRIPTION_DEBUG.warnThreshold) {
      console.warn('websocket subscriptions: ' + rows.length + ' active, more than ' + SUBSCRIPTION_DEBUG.warnThreshold)
    }
  }
}

export const subscriptionRegistry = new SubscriptionRegistry()

window['agSubscriptions'] = () => subscriptionRegistry.printSummary()
window['agDebugSubscriptions'] = (enabled: boolean) => {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0')
  } catch (err) {}
  console.log('websocket subscription debugging ' + (enabled ? 'enabled' : 'disabled'))
}
